// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Edit, Trash2, AlertTriangle } from "lucide-react";
import { useUpcomingEventsForOfficials, useOfficialAssignments, useOfficials, useVenuesForOfficials, useVenueCourts, useOfficialAvailabilityAll } from "@/hooks/useOfficialsData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const statusColors: Record<string, string> = {
  invited: "bg-yellow-500 text-white",
  confirmed: "bg-blue-500 text-white",
  declined: "bg-red-500 text-white",
  checked_in: "bg-green-500 text-white",
  completed: "bg-purple-500 text-white",
  paid: "bg-green-600 text-white",
  no_show: "bg-red-600 text-white",
};

const emptyForm = { official_id: "", venue_id: "", court_id: "", assignment_date: "", shift_start: "", shift_end: "", game_count: "", pay_amount: "", pay_type: "", notes: "" };

const OfficialAssignmentsTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForOfficials();
  const { data: officials } = useOfficials("active");
  const { data: availabilityAll } = useOfficialAvailabilityAll();
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: assignments, isLoading: assLoading } = useOfficialAssignments(selectedEventId);

  const selectedEvent = events?.find((e: any) => e.id === selectedEventId);
  const venueIds = useMemo(() => {
    if (!selectedEvent) return [];
    const ids: string[] = [];
    if (selectedEvent.venue_id) ids.push(selectedEvent.venue_id);
    if (selectedEvent.additional_venue_ids && Array.isArray(selectedEvent.additional_venue_ids)) {
      selectedEvent.additional_venue_ids.forEach((vid: string) => { if (!ids.includes(vid)) ids.push(vid); });
    }
    return ids;
  }, [selectedEvent]);
  const { data: venueData } = useVenuesForOfficials(venueIds);
  const getVenueName = (vid: string) => venueData?.find((v: any) => v.id === vid)?.name || selectedEvent?.venue_name || vid;

  const [filterVenue, setFilterVenue] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const { data: courts } = useVenueCourts(selectedEventId);

  // Bulk select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    return (assignments || []).filter((a: any) => {
      if (filterVenue !== "all" && a.venue_id !== filterVenue) return false;
      if (filterStatus !== "all" && a.status !== filterStatus) return false;
      return true;
    });
  }, [assignments, filterVenue, filterStatus]);

  // Availability warning
  const availWarning = useMemo(() => {
    if (!form.official_id || !form.assignment_date || !availabilityAll) return false;
    const match = availabilityAll.find((a: any) => a.official_id === form.official_id && a.available_date === form.assignment_date);
    return match && !match.is_available;
  }, [form.official_id, form.assignment_date, availabilityAll]);

  const handleOfficialSelect = (officialId: string) => {
    const off = officials?.find((o: any) => o.id === officialId);
    setForm(prev => ({
      ...prev,
      official_id: officialId,
      pay_amount: off?.default_pay_rate?.toString() || prev.pay_amount,
      pay_type: off?.pay_type || prev.pay_type,
    }));
  };

  const handleVenueSelect = (venueId: string) => {
    setForm(prev => ({ ...prev, venue_id: venueId, court_id: "" }));
  };

  const handleOpen = (assignment?: any) => {
    if (assignment) {
      setEditId(assignment.id);
      setForm({
        official_id: assignment.official_id,
        venue_id: assignment.venue_id,
        court_id: assignment.court_id || "",
        assignment_date: assignment.assignment_date || "",
        shift_start: assignment.shift_start || "",
        shift_end: assignment.shift_end || "",
        game_count: assignment.game_count?.toString() || "",
        pay_amount: assignment.pay_amount?.toString() || "",
        pay_type: assignment.pay_type || "",
        notes: assignment.notes || "",
      });
    } else {
      setEditId(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.official_id || !form.venue_id || !selectedEventId) { toast.error("Official and venue are required"); return; }
    const payload = {
      event_id: selectedEventId,
      official_id: form.official_id,
      venue_id: form.venue_id,
      court_id: form.court_id || null,
      assignment_date: form.assignment_date || null,
      shift_start: form.shift_start || null,
      shift_end: form.shift_end || null,
      game_count: form.game_count ? parseInt(form.game_count) : null,
      pay_amount: form.pay_amount ? parseFloat(form.pay_amount) : null,
      pay_type: form.pay_type || null,
      notes: form.notes || null,
    };
    if (editId) {
      const { error } = await supabase.from("manage_official_assignments").update(payload).eq("id", editId);
      if (error) { toast.error("Failed to update"); return; }
      toast.success("Assignment updated");
    } else {
      const { error } = await supabase.from("manage_official_assignments").insert(payload);
      if (error) { toast.error("Failed to create"); return; }
      toast.success("Assignment created");
    }
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["official-assignments", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["officials-event-summary", selectedEventId] });
  };

  const handleStatusChange = async (id: string, status: string) => {
    const { error } = await supabase.from("manage_official_assignments").update({ status }).eq("id", id);
    if (error) { toast.error("Failed to update"); return; }
    toast.success(`Status → ${status}`);
    queryClient.invalidateQueries({ queryKey: ["official-assignments", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["officials-event-summary", selectedEventId] });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this assignment?")) return;
    const { error } = await supabase.from("manage_official_assignments").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Assignment deleted");
    setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    queryClient.invalidateQueries({ queryKey: ["official-assignments", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["officials-event-summary", selectedEventId] });
  };

  const handleBulkStatus = async (status: string) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const { error } = await supabase.from("manage_official_assignments").update({ status }).in("id", ids);
    if (error) { toast.error("Bulk update failed"); return; }
    toast.success(`${ids.length} assignments → ${status}`);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ["official-assignments", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["officials-event-summary", selectedEventId] });
  };

  const toggleSelect = (id: string) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((a: any) => a.id)));
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[280px]">
          <Label>Select Event</Label>
          <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
            <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
            <SelectContent>
              {(events || []).map((e: any) => (
                <SelectItem key={e.id} value={e.id}>{e.name} ({format(parseLocalDate(e.start_date), "MMM d")})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedEventId && (
        <>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Venue</Label>
              <Select value={filterVenue} onValueChange={setFilterVenue}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venueIds.map(vid => <SelectItem key={vid} value={vid}>{getVenueName(vid)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="checked_in">Checked In</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                <Button size="sm" variant="outline" onClick={() => handleBulkStatus("confirmed")}>Mark Confirmed</Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkStatus("paid")}>Mark Paid</Button>
              </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => handleOpen()} className="ml-auto"><Plus className="h-4 w-4 mr-1" />Add Assignment</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>{editId ? "Edit Assignment" : "Add Assignment"}</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                  <div><Label>Official *</Label>
                    <Select value={form.official_id} onValueChange={handleOfficialSelect}>
                      <SelectTrigger><SelectValue placeholder="Select official..." /></SelectTrigger>
                      <SelectContent>{(officials || []).map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.city || ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Venue *</Label>
                    <Select value={form.venue_id} onValueChange={handleVenueSelect}>
                      <SelectTrigger><SelectValue placeholder="Select venue..." /></SelectTrigger>
                      <SelectContent>{venueIds.map(vid => <SelectItem key={vid} value={vid}>{getVenueName(vid)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {courts && courts.length > 0 && (
                    <div><Label>Court</Label>
                      <Select value={form.court_id} onValueChange={(v) => setForm({ ...form, court_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Select court..." /></SelectTrigger>
                        <SelectContent>{courts.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Assignment Date</Label><Input type="date" value={form.assignment_date} onChange={(e) => setForm({ ...form, assignment_date: e.target.value })} /></div>
                  {availWarning && (
                    <div className="flex items-center gap-2 text-yellow-600 text-sm bg-yellow-50 p-2 rounded">
                      <AlertTriangle className="h-4 w-4" /> This official is marked unavailable for this date.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Shift Start</Label><Input type="time" value={form.shift_start} onChange={(e) => setForm({ ...form, shift_start: e.target.value })} /></div>
                    <div><Label>Shift End</Label><Input type="time" value={form.shift_end} onChange={(e) => setForm({ ...form, shift_end: e.target.value })} /></div>
                  </div>
                  <div><Label>Game Count</Label><Input type="number" value={form.game_count} onChange={(e) => setForm({ ...form, game_count: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Pay Amount ($)</Label><Input type="number" step="0.01" value={form.pay_amount} onChange={(e) => setForm({ ...form, pay_amount: e.target.value })} /></div>
                    <div><Label>Pay Type</Label>
                      <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Type..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_game">Per Game</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="flat">Flat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <Button onClick={handleSave} className="w-full">{editId ? "Update" : "Add"} Assignment</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {assLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"><Checkbox checked={selectedIds.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} /></TableHead>
                        <TableHead>Official</TableHead>
                        <TableHead>Venue</TableHead>
                        <TableHead>Court</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Shift</TableHead>
                        <TableHead className="text-right">Games</TableHead>
                        <TableHead className="text-right">Pay</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No assignments found.</TableCell></TableRow>
                      ) : filtered.map((a: any) => (
                        <TableRow key={a.id}>
                          <TableCell><Checkbox checked={selectedIds.has(a.id)} onCheckedChange={() => toggleSelect(a.id)} /></TableCell>
                          <TableCell className="font-medium">{a.official?.name || "—"}</TableCell>
                          <TableCell>{getVenueName(a.venue_id)}</TableCell>
                          <TableCell className="text-sm">{a.court_id || "—"}</TableCell>
                          <TableCell>{a.assignment_date ? format(new Date(a.assignment_date), "MMM d") : "—"}</TableCell>
                          <TableCell className="text-sm">{a.shift_start && a.shift_end ? `${a.shift_start}–${a.shift_end}` : "—"}</TableCell>
                          <TableCell className="text-right">{a.game_count || "—"}</TableCell>
                          <TableCell className="text-right">{a.pay_amount ? `$${Number(a.pay_amount).toLocaleString()}` : "—"}</TableCell>
                          <TableCell>
                            <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                              <SelectTrigger className="w-[130px]">
                                <Badge className={statusColors[a.status] || "bg-gray-400 text-white"}>{a.status === "paid" ? "✓ paid" : a.status?.replace("_", " ")}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="invited">Invited</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                                <SelectItem value="checked_in">Checked In</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="no_show">No Show</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => handleOpen(a)}><Edit className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(a.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default OfficialAssignmentsTab;
